import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Pharmacy, route } from '../constant/constant';
import { ROLE, UserType } from '../interface/interface';
import MoniveaRoadLogo from './HeaderLogo';
import MobileNavBar from './MobileNavBar';
import { UserContext } from '../context/userContext';
import instance from '../config/axios';
import { useAuth } from 'react-oidc-context';
import { UserIcon, ArrowDownIcon, InfoIcon } from '../assets/svgImages';
import { ErxStatusBanner } from './ErxStatusBanner';
import ShoppingCart from './ShoppingCart';
import { WelcomeBanner } from './WelcomeBanner';
import { CartContext } from '../context/cartContext';
import GlobalSearchBar from './GlobalSearch';
import { GuestUserContext } from '../context/guestContext';
import FloatingCartMobile from './FloatingCartMobile';
import FloatingContact from './FloatingContactIcon';
import { NavLink, useNavigate } from 'react-router-dom';
import ContactModal from '../views/ContactModal';
import SubMenu from './SubMenu';

const NavBar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const auth = useAuth();
  const [navigation, setNavigation] = useState<UserType[]>([]);
  const [hideBanner, setHideBanner] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { cart } = useContext(CartContext);
  const {
    setHideDropdown,
    isMobile,
    userDetail,
    iseRxDown,
    unReadItems,
    nativeView,
    searchTerm,
    setSearchTerm,
    searchProducts,
    hasTokenRecords,
    hideDropdown,
  } = useContext(UserContext);
  const { guestSearchProducts } = useContext(GuestUserContext);
  const [activeNav, setActiveNav] = useState<number | null>(null);

  const isPatient = userDetail?.role === ROLE.Patients;

  const [mobilMenuOpen, setMobilMenuOpen] = useState<boolean>(false);
  const isGuest = location.pathname.includes('/guest') && !auth?.user;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setHideDropdown(true);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isGuest) {
      setHideBanner(true);
      return;
    }
    const bannerState = localStorage.getItem('hideBanner_v2');
    if (bannerState) {
      const hide = JSON.parse(bannerState);
      setHideBanner(hide);
    }
  }, []);

  const onMobileMenuClose = () => {
    setMobilMenuOpen(!mobilMenuOpen);
  };

  const handleBannerClose = () => {
    setHideBanner(true);
    localStorage.setItem('hideBanner_v2', JSON.stringify(true));
  };

  const handleBannerOpen = () => {
    setHideBanner(false);
    localStorage.setItem('hideBanner_v2', JSON.stringify(false));
  };

  useEffect(() => {
    if (userDetail === undefined) return;
    setNavigation((route as any)[userDetail.role]);
  }, [userDetail]);

  const RenderInitial = () => {
    return (
      <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary-pink-200">
        <span
          className={`relative font-bold text-base leading-none ${
            process.env.VITE_THEME_TOGGLE === Pharmacy.MEDRELEAF
              ? 'text-white'
              : 'text-primary-blue-500'
          }`}
        >
          {userDetail?.user_initial}
        </span>

        {Boolean(unReadItems) && nativeView && (
          <div className="absolute -right-1 top-2.5 inline-block -translate-y-full -translate-x-0.5 rounded-full bg-primary-red-300 p-1.5 text-xs" />
        )}
      </span>
    );
  };

  const userNavigation = [{ name: 'Sign out', href: '/logout' }];

  if (userDetail && userDetail.role === ROLE.Patients) {
    userNavigation.splice(2, 0, { name: 'My Profile', href: '/profile' });
    if (!isMobile) {
      userNavigation.splice(3, 0, {
        name: 'My WishList',
        href: '/product/wishlist',
      });
      userNavigation.splice(4, 0, {
        name: 'Medications',
        href: '/prescriptions',
      });
      userNavigation.splice(5, 0, {
        name: 'Dosage Diary',
        href: '/dosage-diary',
      });
      userNavigation.splice(6, 0, {
        name: 'Monthly Limits',
        href: '/monthly-limit',
      });
      userNavigation.splice(7, 0, {
        name: 'Order History',
        href: '/orders',
      });
    } else {
      userNavigation.splice(3, 0, {
        name: 'My WishList',
        href: '/product/wishlist',
      });
    }

    const signOut = userNavigation.splice(
      userNavigation.findIndex((item) => item.name === 'Sign out'),
      1,
    )[0];

    if (nativeView) {
      userNavigation.splice(4, 0, {
        name: 'Inbox',
        href: '/notification-inbox',
      });
    }
    userNavigation.push(signOut);
  }

  const stopImpersonating = async () => {
    instance({
      method: 'GET',
      url: '/api/v1/user/impersonate_end',
      headers: {
        Authorization: `Bearer ${user?.access_token}`,
      },
      withCredentials: true,
    })
      .then((resp) => {
        if (resp.status === 200) {
          localStorage.removeItem('impersonateUser');
          auth.removeUser();
          navigate('/home');
        }
      })
      .catch((e: unknown) => {
        console.error('D> error fetching ending impersonation: ', e);
      });
  };

  function searchHandler(value: string) {
    setSearchTerm(value);
  }

  function getSubNavHandler(item: UserType) {
    if (!item.subNav) return undefined;

    return () => setHideDropdown(true);
  }

  const isSubNavActive = (subNavItems: UserType[]) => {
    return subNavItems.some(
      (subNavItem) => location.pathname === subNavItem.href,
    );
  };

  if (!isGuest && userDetail === undefined) {
    return (
      <div className="navbar sticky top-0 z-50 bg-primary-blue-500 h-[88px] md:h-[104px]" />
    );
  }

  return (
    <>
      <Disclosure
        ref={menuRef}
        as="nav"
        className={`${process.env.VITE_THEME_TOGGLE === Pharmacy.MEDRELEAF ? 'bg-white shadow-2xl' : 'bg-primary-blue-500'} navbar sticky top-0 z-50`}
      >
        {() => (
          <>
            <div
              className={`mx-auto ${isPatient || isGuest ? 'container-standard' : ''}`}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 items-center gap-4 md:gap-0 md:px-4 py-6">
                {userDetail && (
                  <div
                    className={`${userDetail ? 'p-1' : 'py-7 px-5'} 3xl:p-10 items-center 
              justify-between header-container grid grid-cols-2 lg:grid-cols-3 gap-3 ${isPatient || isGuest ? 'hidden' : 'block'}`}
                  >
                    {navigation?.length ? (
                      <div className="hidden lg:flex items-center">
                        <div className="hidden md:block">
                          <div className="flex items-baseline space-x-4">
                            {navigation
                              ?.filter((x) => !x.mobileOnly)
                              ?.map((item: UserType, index: number) => (
                                <div
                                  key={index}
                                  onMouseEnter={() => setActiveNav(index)}
                                  onMouseLeave={() => setActiveNav(null)}
                                >
                                  <NavLink
                                    onClick={getSubNavHandler(item)}
                                    to={item.href}
                                    className={({ isActive }) =>
                                      `text-secondary-pink-100 fill-secondary-pink-100 lg:px-1 xl:px-2 py-1 2xl:text-base
                           hover:text-secondary-pink-300 hover:fill-secondary-pink-300 font-semibold relative 2.5xl:px-3 3xl:text-xl flex items-center justify-center align-middle'
                          ${
                            isSubNavActive(item.subNav || [])
                              ? 'text-secondary-pink-300 fill-secondary-pink-300'
                              : 'text-secondary-pink-100 fill-secondary-pink-100'
                          } ${isActive ? 'text-secondary-pink-300 fill-secondary-pink-300' : 'text-secondary-pink-100 fill-secondary-pink-100'}
                        `
                                    }
                                  >
                                    <span className="flex align-middle items-center">
                                      {item.name}{' '}
                                    </span>
                                    {item.name.toLowerCase() === 'tokens' &&
                                      hasTokenRecords && (
                                        <span className="absolute bg-secondary-pink-300 h-2.5 w-2.5 rounded-full top-0 right-0"></span>
                                      )}
                                    {item.subNav && (
                                      <ArrowDownIcon
                                        className={`${hideDropdown ? `rotate-0` : `rotate-180`} w-3 h-3 ml-2.5`}
                                      />
                                    )}
                                  </NavLink>
                                  {!isPatient &&
                                    item.subNav &&
                                    activeNav === index && (
                                      <div
                                        className="absolute bg-white rounded-md shadow p-1 z-50"
                                        onMouseEnter={() => setActiveNav(index)}
                                        onMouseLeave={() => setActiveNav(null)}
                                      >
                                        {item.subNav.map(
                                          (subNavItem, subIndex) => (
                                            <NavLink
                                              key={subIndex}
                                              to={subNavItem.href}
                                              className={`block px-4 py-2 text-sm text-primary-blue-500 font-semibold 
                                  hover:bg-secondary-pink-100 hover:rounded 
                                  ${process.env.VITE_THEME_TOGGLE === Pharmacy.MEDRELEAF ? 'hover:text-white' : 'hover:text-primary-blue-500'}`}
                                            >
                                              {subNavItem.name}
                                            </NavLink>
                                          ),
                                        )}
                                      </div>
                                    )}
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
                {/* Logo */}
                <div id="moniveaRoadLogo" className="flex-shrink-0">
                  <MoniveaRoadLogo
                    userToken={
                      user?.access_token ? user?.access_token : undefined
                    }
                    isGuest={isGuest}
                    isAdmin={userDetail?.role !== ROLE.Patients && !isGuest}
                  />
                </div>
                {/* Desktop search - grows to fill available space */}
                {((auth.user?.profile?.groups as Array<string>)?.includes(
                  ROLE.Patients,
                ) ||
                  isGuest) && (
                  <div className={`hidden md:flex flex-1 justify-center px-4}`}>
                    <div className="w-full max-w-md">
                      <GlobalSearchBar
                        products={
                          isGuest ? guestSearchProducts : searchProducts
                        }
                        onSearch={(value) => searchHandler(value)}
                        searchTerm={searchTerm}
                        isGuest={isGuest}
                      />
                    </div>
                  </div>
                )}
                {/* Right side actions */}
                <div className="flex items-center gap-3 flex-shrink-0 justify-end">
                  {localStorage.getItem('impersonateUser') && !isGuest && (
                    <button
                      type="button"
                      className="hidden md:inline-flex items-center rounded-full bg-primary-red-400 px-4 py-2 text-white font-semibold text-xs sm:text-sm hover:bg-primary-red-500 transition-colors"
                      onClick={() => {
                        stopImpersonating();
                        sessionStorage.removeItem('homeTab');
                      }}
                    >
                      Stop Impersonating
                      <span className="sr-only">Stop Impersonating</span>
                    </button>
                  )}

                  {hideBanner && (
                    <button
                      type="button"
                      onClick={handleBannerOpen}
                      className="flex items-center justify-center hover:opacity-80 transition-opacity"
                      aria-label="Support"
                    >
                      <InfoIcon
                        className={`h-7 w-7 cursor-pointer border-2 rounded-2xl ${isGuest ? 'me-1' : ''}`}
                      />
                    </button>
                  )}

                  <div className="flex justify-end items-center">
                    {isGuest ? <FloatingCartMobile /> : <></>}
                  </div>

                  {(auth.user?.profile?.groups as Array<string>)?.includes(
                    ROLE.Patients,
                  ) ? (
                    <ShoppingCart />
                  ) : (
                    <></>
                  )}

                  {(auth.user?.profile?.groups as Array<string>)?.includes(
                    ROLE.Patients,
                  ) && (
                    <button
                      type="button"
                      className="md:hidden flex items-center justify-center pt-[2px]"
                      onClick={() => onMobileMenuClose()}
                      aria-label="Open menu"
                    >
                      <UserIcon className="w-6 h-6 " />
                    </button>
                  )}
                  {(isMobile || nativeView) && !isGuest && isPatient && (
                    <ContactModal hideBanner={hideBanner} />
                  )}

                  {userDetail?.name && !isPatient && (
                    <Menu as="div" className="relative ml-3 flex items-center">
                      <div className="relative">
                        <Menu.Button className="relative flex  shadow-none items-center rounded-full bg-indigo-600 text-sm">
                          <span className="absolute -inset-1.5" />
                          <span className="sr-only">Open user menu</span>
                          <RenderInitial />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 top-full z-40 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {userNavigation.map((item) => (
                              <Menu.Item key={item.name}>
                                {({ active }) => (
                                  <a
                                    href={item.href}
                                    className={`${active ? 'bg-gray-100' : ''} flex justify-between items-center px-4 py-2 text-sm text-gray-700`}
                                  >
                                    <span>{item.name}</span>
                                    {item.name === 'Inbox' && unReadItems && (
                                      <span className="inline-block rounded-full bg-primary-red-300 p-1 text-xs" />
                                    )}
                                  </a>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Transition>
                      </div>
                    </Menu>
                  )}

                  {userDetail?.name &&
                    (auth.user?.profile?.groups as Array<string>)?.includes(
                      ROLE.Patients,
                    ) &&
                    !isMobile && (
                      <Menu as="div" className="relative flex items-center">
                        <div className="relative">
                          <Menu.Button className="flex items-center">
                            <span className="sr-only">Open user menu</span>
                            {/* <RenderInitial /> */}
                            <UserIcon className="w-6 h-6 " />
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 top-full z-40 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {userNavigation.map((item) => (
                                <Menu.Item key={item.name}>
                                  {({ active }) => (
                                    <a
                                      href={item.href}
                                      className={`${active ? 'hover:bg-primary-blue-500 shadow-md' : ''}  transition-transform duration-200 ease-in-out hover:scale-105 flex justify-between items-center px-4 py-2 text-sm text-primary-blue-600 hover:text-white text-gray-700`}
                                    >
                                      <span>{item.name}</span>
                                      {item.name === 'Inbox' && unReadItems && (
                                        <span className="inline-block rounded-full bg-primary-red-300 p-1 text-xs" />
                                      )}
                                    </a>
                                  )}
                                </Menu.Item>
                              ))}
                            </Menu.Items>
                          </Transition>
                        </div>
                      </Menu>
                    )}
                </div>
              </div>
            </div>

            {/* Mobile: search + profile row */}
            {!(auth.user?.profile?.groups as Array<string>)?.includes(
              ROLE.Admin,
            ) && (
              <div className="flex items-center w-full gap-3 md:hidden px-4 pb-4 z-[99]">
                <div className="flex-1 min-w-0">
                  <GlobalSearchBar
                    products={isGuest ? guestSearchProducts : searchProducts}
                    onSearch={(value) => searchHandler(value)}
                    searchTerm={searchTerm}
                    isGuest={isGuest}
                  />
                </div>
              </div>
            )}

            {/* Mobile: stop impersonating */}
            {localStorage.getItem('impersonateUser') && !isGuest && (
              <div className="flex justify-center md:hidden px-4 pb-4">
                <button
                  type="button"
                  className="flex items-center justify-center p-3 rounded-full bg-primary-red-400 text-white font-semibold text-sm w-full active:bg-primary-red-500 transition-colors"
                  onClick={() => stopImpersonating()}
                >
                  Stop Impersonating
                  <span className="sr-only">Stop Impersonating</span>
                </button>
              </div>
            )}

            {!isGuest && (
              <MobileNavBar
                nav={navigation}
                cartLineItemsExists={cart?.line_items?.length}
              />
            )}

            {process.env.VITE_SHOW_BANNER === 'TRUE' && (
              <WelcomeBanner
                hideBanner={hideBanner}
                handleBannerClose={handleBannerClose}
                nativeView={nativeView}
              />
            )}

            {/* {(isMobile || nativeView) && (
              <div
                className={`cursor-pointer relative ${hideBanner ? 'bg-pink-100 border-primary-blue-500 border-b-4' : ''}`}
                onClick={handleBannerOpen}
              >
                {hideBanner && (
                  <div className="absolute left-1/2 -bottom-0.5 translate-y-1/2 -translate-x-1/2 bg-pink-100 px-1 rounded-full border-2 border-primary-blue-500 shadow-sm">
                    <ChevronDownIcon className="w-4 h-4 text-primary-blue-500 stroke-[4]" />
                  </div>
                )}
              </div>
            )} */}

            {iseRxDown && <ErxStatusBanner />}
          </>
        )}
      </Disclosure>

      {(isGuest || isPatient) && isMobile ? (
        <SubMenu
          products={isGuest ? guestSearchProducts : searchProducts}
          mobileMenuOpen={mobilMenuOpen}
          onMobileMenuClose={onMobileMenuClose}
          isGuest={isGuest}
          isMobile={isMobile && nativeView}
        />
      ) : (
        <></>
      )}
      {!isGuest && !isMobile && !nativeView && isPatient ? (
        <FloatingContact />
      ) : (
        <></>
      )}
    </>
  );
};

export default NavBar;
